// =============================================================================
// features/configuracoes/ai-console/decisions/DecisionFilters.tsx
//
// Painel de filtros para a lista de decisões do agente de IA.
// Filtros (os que a API aceita — listDecisionsQuerySchema): conversation_id,
// lead_id, node_name. Data/intent/modelo não existem na API (F58-S04).
// Estado sincronizado via URL querystring (chamador gerencia URLSearchParams).
//
// DS: inputs com inset shadow, border-border-strong, hover/focus em azul.
// Área clicável mínima 40px (WCAG). Todos os inputs com label semântica.
// =============================================================================

import * as React from 'react';
import { z } from 'zod';

import { LeadCombobox } from '../../../../components/comboboxes/LeadCombobox';
import { cn } from '../../../../lib/cn';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface FilterValues {
  conversation_id: string;
  lead_id: string;
  node_name: string;
}

/** A API valida conversation_id como UUID — texto livre devolveria 400. */
export function isValidConversationId(value: string): boolean {
  return z.string().uuid().safeParse(value).success;
}

interface DecisionFiltersProps {
  values: FilterValues;
  onChange: (key: keyof FilterValues, value: string) => void;
  onReset: () => void;
}

// ─── Primitivo de campo ───────────────────────────────────────────────────────

function FilterField({
  label,
  id,
  children,
}: {
  label: string;
  id: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={id}
        className="font-sans font-medium text-ink-3 uppercase tracking-widest"
        style={{ fontSize: '0.65rem' }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

// Classes de input reutilizáveis — DS canônico: inset shadow + border-strong
const inputCls = cn(
  'h-10 w-full px-3',
  'font-sans text-sm text-ink',
  'rounded-sm border border-border-strong',
  'bg-surface-1',
  'shadow-[inset_0_1px_2px_var(--border-inner-dark,rgba(0,0,0,0.06))]',
  'placeholder:text-ink-4',
  'focus:outline-none focus:border-azul',
  'focus:shadow-[0_0_0_3px_rgba(27,58,140,0.12),inset_0_1px_2px_rgba(0,0,0,0.06)]',
  'transition-[border-color,box-shadow] duration-[150ms] ease-out',
  'disabled:opacity-50 disabled:cursor-not-allowed',
);

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * Painel de filtros da lista de decisões do agente.
 * onChange notifica o pai que persiste na URL.
 */
export function DecisionFilters({
  values,
  onChange,
  onReset,
}: DecisionFiltersProps): React.JSX.Element {
  const hasActiveFilter = Object.values(values).some((v) => v.length > 0);
  const conversationInvalid =
    values.conversation_id.length > 0 && !isValidConversationId(values.conversation_id);

  return (
    <div
      className="rounded-lg border border-border p-4 flex flex-col gap-4"
      style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-1)' }}
      aria-label="Filtros de decisões"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px] max-w-sm">
          <FilterField label="Conversa ID" id="filter-conversation-id">
            <input
              id="filter-conversation-id"
              type="text"
              className={cn(inputCls, 'font-mono', conversationInvalid && 'border-danger')}
              placeholder="UUID da conversa"
              value={values.conversation_id}
              onChange={(e) => onChange('conversation_id', e.target.value.trim())}
              aria-label="Filtrar por ID de conversa"
              aria-invalid={conversationInvalid}
              aria-describedby={conversationInvalid ? 'filter-conversation-id-error' : undefined}
            />
          </FilterField>
          {conversationInvalid && (
            <p
              id="filter-conversation-id-error"
              className="mt-1 font-sans text-xs text-danger"
              role="alert"
            >
              Informe um UUID válido — o filtro não foi aplicado.
            </p>
          )}
        </div>

        <div className="flex-1 min-w-[220px] max-w-sm">
          <FilterField label="Lead" id="filter-lead-id">
            <LeadCombobox
              value={values.lead_id}
              onChange={(id) => onChange('lead_id', id)}
              label=""
              placeholder="Buscar lead…"
            />
          </FilterField>
        </div>

        <div className="flex-1 min-w-[160px] max-w-xs">
          <FilterField label="Nó" id="filter-node-name">
            <input
              id="filter-node-name"
              type="text"
              className={inputCls}
              placeholder="ex: classify_intent"
              value={values.node_name}
              onChange={(e) => onChange('node_name', e.target.value)}
              aria-label="Filtrar por nó do grafo"
              maxLength={120}
            />
          </FilterField>
        </div>

        {hasActiveFilter && (
          <button
            type="button"
            onClick={onReset}
            className={cn(
              'h-10 px-4 rounded-sm border border-border self-end',
              'font-sans text-sm font-medium text-ink-3',
              'bg-surface-1 hover:border-danger hover:text-danger',
              'transition-[border-color,color] duration-[150ms] ease-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
            )}
            aria-label="Limpar todos os filtros"
          >
            Limpar filtros
          </button>
        )}
      </div>
    </div>
  );
}
